import {Game, GameStatus, getStatusDisplay, getStatusClass} from "./model/Game.ts";
import {useEffect, useState} from "react";
import {Repository, Result} from "./Repository.ts";
import {Play} from "./model/Play.ts";
import {Athlete} from "./model/Athlete.ts";
import {Team} from "./model/Team.ts";
import LineupPmGrid from "./LineupPmGrid.tsx";
import ReactGA from "react-ga4";
import './GameCard.css';

export default function GameScreen(props: { game: Game, repo: Repository }) {

    const [plays, setPlays] = useState<Play[] | null>(null);
    const [athletes, setAthletes] = useState<Athlete[] | null>(null);
    const [teams, setTeams] = useState<Team[] | null>(null);
    const [game, setGame] = useState<Game>(props.game);
    const [error, setError] = useState<string | null>(null);
    const [refresh, setRefresh] = useState<number>(0);
    const [zoomLevel, setZoomLevel] = useState<number>(0.5);

    useEffect(() => {
        ReactGA.event({
            category: "Game",
            action: "View Game Screen",
            label: props.game.id, // optional
        });
    }, [props.game.id]);

    async function fetchPlays() {
        const result: Result<Play[]> = await props.repo.getPlays(game.id, true);
        if (result.type === 'success') {
            setPlays(result.value);
        } else {
            setError(result.error);
        }
    }
    useEffect(() => {
        fetchPlays();
    }, [game.id, refresh]);

    async function fetchGame() {
        const result: Result<Game> = await props.repo.getGame(game.id, true);
        if (result.type === 'success') {
            setGame(result.value);
        } else {
            setError(result.error);
        }
    }
    useEffect(() => {
        fetchGame();
    }, [refresh]);

    function triggerRefresh() {
        setRefresh(refresh + 1);
    }

    function handleZoomIn() {
        setZoomLevel(prev => Math.min(prev * 1.1, 12.0));
    }

    function handleZoomOut() {
        setZoomLevel(prev => Math.max(prev / 1.1, 0.1));
    }

    useEffect(() => {
        async function fetchData() {
            const result: Result<Athlete[]> = await props.repo.getPlayers();
            if (result.type === 'success') {
                setAthletes(result.value);
            } else {
                setError(result.error);
            }
        }

        fetchData();
    }, [setAthletes, setError]);

    useEffect(() => {
        async function fetchData() {
            const result: Result<Team[]> = await props.repo.getTeams();
            if (result.type === 'success') {
                setTeams(result.value);
            } else {
                setError(result.error);
            }
        }

        fetchData();
    }, [setTeams, setError]);

    let homeTeam = teams?.find(t => t.id === game.homeTeamId)!
    let awayTeam = teams?.find(t => t.id === game.awayTeamId)!

    const isFinal = game.status === GameStatus.STATUS_FINAL;
    const homeWon = isFinal && game.homeScore > game.awayScore;
    const awayWon = isFinal && game.awayScore > game.homeScore;

    if (plays != null && athletes != null && homeTeam != null && awayTeam != null) {
        return (
            <div>
                <div>
                    <div>{new Date(game.startDate).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div>
                    <div className="game-header">
                        <h2>{homeTeam?.displayName} {game.homeScore} {homeWon && <span className="win-arrow">◀</span>}</h2>
                        <h2>@</h2>
                        <h2>{awayWon && <span className="win-arrow">▶</span>} {awayTeam?.displayName} {game.awayScore}</h2>

                        <div className={getStatusClass(game.status)}>{getStatusDisplay(game.status)}</div>

                        {game.status == GameStatus.STATUS_IN_PROGRESS || game.status == GameStatus.STATUS_HALFTIME ?
                            <button onClick={triggerRefresh}>↻</button> : null}

                        <button onClick={handleZoomIn}>🔍+</button>
                        <button onClick={handleZoomOut}>🔍−</button>
                    </div>
                </div>

                <LineupPmGrid game={game} plays={plays!} players={athletes!} homeTeam={homeTeam} awayTeam={awayTeam} zoomLevel={zoomLevel}/>
            </div>
        )
    } else {
        return <div>Loading...</div>
    }
}
